import React, { useState } from "react";
import { Link } from "react-router-dom";

type MediaItemDraft = {
  id: string;
  type: "PHOTO" | "VIDEO";
  title: string;
  url: string;
  isCover: boolean;
  sortOrder: string;
};

const cardStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 16,
  padding: "1rem",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  border: "1px solid #d1d5db",
  borderRadius: 12,
  padding: "0.8rem 0.9rem",
  fontSize: "0.96rem",
  background: "#fff",
};

const fieldWrapStyle: React.CSSProperties = {
  display: "grid",
  gap: "0.45rem",
};

const labelStyle: React.CSSProperties = {
  fontSize: "0.9rem",
  fontWeight: 600,
  color: "#334155",
};

const secondaryButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  textDecoration: "none",
  border: "1px solid #d1d5db",
  borderRadius: 12,
  background: "#fff",
  color: "#111827",
  padding: "0.8rem 1rem",
  fontWeight: 700,
};

const primaryButtonStyle: React.CSSProperties = {
  border: "none",
  borderRadius: 12,
  background: "#111827",
  color: "#fff",
  padding: "0.8rem 1rem",
  fontWeight: 700,
  cursor: "pointer",
};

const createItem = (): MediaItemDraft => ({
  id: `media-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  type: "PHOTO",
  title: "",
  url: "",
  isCover: false,
  sortOrder: "1",
});

export default function ProviderPortfolioStepPage() {
  const [items, setItems] = useState<MediaItemDraft[]>([createItem()]);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const updateItem = <K extends keyof MediaItemDraft>(
    id: string,
    key: K,
    value: MediaItemDraft[K],
  ) => {
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, [key]: value } : item)),
    );
  };

  const addItem = () => {
    setItems((current) => [...current, createItem()]);
  };

  const removeItem = (id: string) => {
    setItems((current) => (current.length === 1 ? current : current.filter((i) => i.id !== id)));
  };

  const handleSave = (event: React.FormEvent) => {
    event.preventDefault();
    setSavedAt(new Date().toLocaleTimeString());
  };

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <section style={cardStyle}>
        <h2 style={{ margin: 0, color: "#0f172a", fontSize: "1.4rem" }}>
          Portfolio
        </h2>
        <p style={{ margin: "0.5rem 0 0", color: "#475569", lineHeight: 1.7 }}>
          Add photos and videos that staff can review for profile completeness and
          go-live readiness.
        </p>
      </section>

      <form onSubmit={handleSave} style={{ display: "grid", gap: "1rem" }}>
        {items.map((item, index) => (
          <section
            key={item.id}
            style={{
              ...cardStyle,
              background: "#f8fafc",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "0.75rem",
                alignItems: "center",
                marginBottom: "1rem",
                flexWrap: "wrap",
              }}
            >
              <div>
                <h3 style={{ margin: 0, color: "#0f172a" }}>
                  Media item {index + 1}
                </h3>
                <p style={{ margin: "0.35rem 0 0", color: "#64748b" }}>
                  Use URLs for now to seed test data quickly.
                </p>
              </div>

              <button
                type="button"
                onClick={() => removeItem(item.id)}
                style={{
                  border: "1px solid #fecaca",
                  background: "#fff1f2",
                  color: "#9f1239",
                  borderRadius: 10,
                  padding: "0.65rem 0.9rem",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Remove
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gap: "0.85rem",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              }}
            >
              <div style={fieldWrapStyle}>
                <label style={labelStyle}>Type</label>
                <select
                  style={inputStyle}
                  value={item.type}
                  onChange={(event) =>
                    updateItem(item.id, "type", event.target.value as "PHOTO" | "VIDEO")
                  }
                >
                  <option value="PHOTO">PHOTO</option>
                  <option value="VIDEO">VIDEO</option>
                </select>
              </div>

              <div style={fieldWrapStyle}>
                <label style={labelStyle}>Title</label>
                <input
                  style={inputStyle}
                  value={item.title}
                  onChange={(event) => updateItem(item.id, "title", event.target.value)}
                  placeholder="Bridal makeup portfolio"
                />
              </div>

              <div style={fieldWrapStyle}>
                <label style={labelStyle}>Sort order</label>
                <input
                  style={inputStyle}
                  type="number"
                  min={1}
                  value={item.sortOrder}
                  onChange={(event) =>
                    updateItem(item.id, "sortOrder", event.target.value)
                  }
                />
              </div>
            </div>

            <div style={{ ...fieldWrapStyle, marginTop: "0.85rem" }}>
              <label style={labelStyle}>Media URL</label>
              <input
                style={inputStyle}
                value={item.url}
                onChange={(event) => updateItem(item.id, "url", event.target.value)}
                placeholder="https://..."
              />
            </div>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                marginTop: "0.9rem",
                color: "#334155",
                fontWeight: 600,
              }}
            >
              <input
                type="checkbox"
                checked={item.isCover}
                onChange={(event) =>
                  updateItem(item.id, "isCover", event.target.checked)
                }
              />
              Mark as cover item
            </label>
          </section>
        ))}

        <section style={cardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "0.75rem",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div style={{ color: "#475569" }}>
              {savedAt ? `Draft saved locally at ${savedAt}` : "No local save yet"}
            </div>

            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
              <button type="button" onClick={addItem} style={secondaryButtonStyle}>
                Add media item
              </button>
              <button type="submit" style={primaryButtonStyle}>
                Save portfolio
              </button>
              <Link to="/provider/onboarding/review" style={secondaryButtonStyle}>
                Continue to review
              </Link>
            </div>
          </div>
        </section>
      </form>
    </div>
  );
}